/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  autoUpdate,
  flip,
  offset,
  size,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
  useRole,
  safePolygon,
  FloatingPortal,
  useFloatingNodeId,
  FloatingNode,
  FloatingTree,
} from "@floating-ui/react";
import type { Table } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Checkbox, Icon, InputField, DateInput } from "@app-components";
import styles from "@/styles/TableFilter.module.scss";
import {
  dateFilterFn,
  ALL_WEEKDAYS,
  getWeekdayFromNumber,
  DateFilterValue,
} from "@/lib/utils";
import { useIsMobile } from "@/hooks/useIsMobile";
import { endOfDay, format, startOfDay } from "date-fns";
import { NO_VALUE_MARKER, HAS_VALUE_MARKER } from "@/lib/constants";
import { DateString } from "@/types";
import type { FilterConfig } from "./Table";

// TODO: future improvement: make the menu more accessible by adding keyboard navigation (like our regular dropdown menus)

interface TableFilterProps<T> {
  table: Table<T>;
  disabled?: boolean;
  rerenderDeps?: any[];
}

export function TableFilter<T>({
  table,
  disabled,
  rerenderDeps,
}: TableFilterProps<T>) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const { isMobile } = useIsMobile();
  const [activeColumnId, setActiveColumnId] = useState<string | null>(null);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: (open) => {
      setIsOpen(open);
      if (!open) {
        setActiveColumnId(null);
      }
    },
    middleware: [
      offset(4),
      flip(),
      shift({ padding: 10 }),
      size({
        apply({ availableHeight, availableWidth, elements }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${Math.min(availableHeight - 20, 400)}px`,
            maxWidth: `${availableWidth - 20}px`,
          });
        },
      }),
    ],
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  const columns = useMemo(() => {
    return table
      .getAllColumns()
      .filter((column) => {
        if (["select", "selectCol", "actions"].includes(column.id))
          return false;

        // Check if column has filterConfig with exclude flag
        const filterConfig = (column.columnDef.meta as any)?.filterConfig as
          | FilterConfig<T>
          | undefined;
        if (filterConfig?.exclude) return false;

        // Allow hidden columns with explicit includeInFilter flag
        if (!column.getIsVisible() && !filterConfig?.includeInFilter)
          return false;

        return true;
      })
      .map((col) => {
        const filterConfig = (col.columnDef.meta as any)?.filterConfig as
          | FilterConfig<T>
          | undefined;
        return {
          column: col,
          ...filterConfig,
        };
      });
  }, [table, ...(rerenderDeps ?? [])]);

  const nodeId = useFloatingNodeId();
  const activeColumnItem = useMemo(
    () => columns.find((c) => c.column.id === activeColumnId),
    [columns, activeColumnId],
  );
  const activeColumn = activeColumnItem?.column;

  return (
    <FloatingTree>
      <FloatingNode id={nodeId}>
        <div className={styles.container}>
          <div
            ref={refs.setReference}
            {...getReferenceProps({
              onClick: (e) => e.stopPropagation(),
            })}
          >
            <Button size="sm" icon="filter_list" secondary disabled={disabled}>
              {t("FILTER")}
            </Button>
          </div>
          {isOpen && (
            <FloatingPortal>
              <div
                ref={refs.setFloating}
                style={floatingStyles}
                className={`${styles.menu} ${isMobile && activeColumn ? styles.subMenu : ""}`}
                {...getFloatingProps()}
              >
                {isMobile ? (
                  activeColumn ? (
                    // Mobile Drilldown View
                    <div style={{ paddingBottom: "var(--wlcm-spacing-sm)" }}>
                      <div
                        className={styles.menuItem}
                        style={{
                          justifyContent: "unset",
                          fontWeight: "bold",
                          gap: "var(--wlcm-spacing-sm)",
                          paddingTop: "var(--wlcm-spacing-sm)",
                        }}
                        onClick={() => setActiveColumnId(null)}
                      >
                        <Icon
                          name="chevron_left"
                          style={{ fontSize: "16px", marginRight: "4px" }}
                        />
                        {(activeColumn.columnDef.meta as { label?: string })
                          ?.label ||
                          (typeof activeColumn.columnDef.header === "string"
                            ? activeColumn.columnDef.header
                            : activeColumn.id)}
                      </div>
                      <FilterMenuContent
                        {...activeColumnItem}
                        filterValue={activeColumn.getFilterValue()}
                      />
                    </div>
                  ) : (
                    // Mobile Main View
                    <>
                      {columns.map(({ column }) => (
                        <div
                          key={column.id}
                          className={styles.menuItem}
                          onClick={() => setActiveColumnId(column.id)}
                        >
                          <span>
                            {(column.columnDef.meta as { label?: string })
                              ?.label ||
                              (typeof column.columnDef.header === "string"
                                ? column.columnDef.header
                                : column.id)}
                          </span>
                          <Icon
                            name="chevron_right"
                            style={{ fontSize: "16px" }}
                          />
                        </div>
                      ))}
                      {columns.length === 0 && (
                        <div className={styles.noColumns}>
                          {t("NO_FILTERS_AVAILABLE")}
                        </div>
                      )}
                    </>
                  )
                ) : (
                  // Desktop View
                  <>
                    {columns.map(({ column, ...config }) => (
                      <FilterColumnItem
                        key={column.id}
                        column={column}
                        {...config}
                      />
                    ))}
                    {columns.length === 0 && (
                      <div className={styles.noColumns}>
                        {t("NO_FILTERS_AVAILABLE")}
                      </div>
                    )}
                  </>
                )}
              </div>
            </FloatingPortal>
          )}
        </div>
      </FloatingNode>
    </FloatingTree>
  );
}

function FilterColumnItem<T>({
  column,
  ...config
}: {
  column: any;
} & FilterConfig<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [isInteracting, setIsInteracting] = useState(false);
  const nodeId = useFloatingNodeId();

  const { refs, floatingStyles, context } = useFloating({
    nodeId,
    open: isOpen,
    onOpenChange: (open, _event, reason) => {
      if (!open && isInteracting && reason === "hover") {
        return;
      }
      setIsOpen(open);
    },
    placement: "right-start",
    middleware: [
      offset({ mainAxis: 0, alignmentAxis: -4 }),
      flip(),
      shift({ padding: 10 }),
      size({
        apply({ availableHeight, availableWidth, elements }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${Math.min(availableHeight - 20, 400)}px`,
            maxWidth: `${availableWidth - 20}px`,
          });
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, {
    handleClose: safePolygon(),
    delay: { open: 75 },
    enabled: !isInteracting,
  });
  const role = useRole(context);
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    role,
    dismiss,
  ]);

  return (
    <FloatingNode id={nodeId}>
      <div
        ref={refs.setReference}
        {...getReferenceProps()}
        className={`${styles.menuItem} ${isOpen ? styles.open : ""}`}
      >
        <span>
          {(column.columnDef.meta as { label?: string })?.label ||
            (typeof column.columnDef.header === "string"
              ? column.columnDef.header
              : column.id)}
        </span>
        <Icon name="chevron_right" style={{ fontSize: "16px" }} />

        {isOpen && (
          <FloatingPortal>
            <div
              ref={refs.setFloating}
              style={floatingStyles}
              className={`${styles.menu} ${styles.subMenu}`}
              {...getFloatingProps()}
            >
              <FilterMenuContent
                column={column}
                onInteractionChange={setIsInteracting}
                filterValue={column.getFilterValue()}
                {...config}
              />
            </div>
          </FloatingPortal>
        )}
      </div>
    </FloatingNode>
  );
}

function FilterMenuContent<T>({
  column,
  onInteractionChange,
  filterValue,
  getLabel,
  forceBoolean,
  mutuallyExclusive,
  optionValues,
}: {
  column: any;
  onInteractionChange?: (isInteracting: boolean) => void;
  filterValue?: any;
} & FilterConfig<T>) {
  const { t, i18n } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");

  const isEmailColumn = column.id === "email";

  const uniqueValues = useMemo(() => {
    // For forceBoolean columns, always return only two options: "Has value" and "Has no value"
    if (forceBoolean) {
      return [HAS_VALUE_MARKER, NO_VALUE_MARKER];
    }

    let rawValues;
    if (optionValues) {
      rawValues = optionValues;
    } else {
      const facedValues = column.getFacetedUniqueValues();
      rawValues = Array.from(facedValues.keys());
    }

    // For email columns, extract unique domains
    if (isEmailColumn) {
      const domains = new Set<string>();
      rawValues.forEach((email) => {
        if (typeof email === "string" && email.includes("@")) {
          const domain = email.split("@")[1];
          if (domain) {
            domains.add(`@${domain}`);
          }
        }
      });
      return Array.from(domains).sort();
    }

    // Normalize null, undefined, and empty string to a single filter option of "No value"
    const normalizedValues = rawValues.map((val) =>
      val == null || val === "" ? NO_VALUE_MARKER : val,
    );
    // If any value is boolean, make sure we always show both "Yes" and "No" options for consistency, even if the value doesn't exist in the table
    if (normalizedValues.some((v) => typeof v === "boolean")) {
      normalizedValues.push(...[true, false]);
    }
    const uniqueSet = new Set(normalizedValues);
    return Array.from(uniqueSet).sort();
  }, [column, isEmailColumn, forceBoolean]);

  const filteredValues = useMemo(() => {
    if (!searchQuery) return uniqueValues;
    return uniqueValues.filter((value) => {
      let valStr;
      if (getLabel) {
        valStr = String(getLabel(value, column)).toLowerCase();
      } else {
        valStr = String(value).toLowerCase();
        // Handle boolean translation for search
        if (typeof value === "boolean") {
          const boolLabel = value ? t("YES") : t("NO");
          if (boolLabel.toLowerCase().includes(searchQuery.toLowerCase())) {
            return true;
          }
        }
      }
      return valStr.includes(searchQuery.toLowerCase());
    });
  }, [uniqueValues, searchQuery, t, getLabel]);

  const isDateColumn = column.columnDef.filterFn === dateFilterFn;
  const dateFilterValue: DateFilterValue = (() => {
    if (!filterValue) return {};
    const val = filterValue as any;
    const start = val.start ? new Date(val.start) : undefined;
    const end = val.end ? new Date(val.end) : undefined;
    return {
      start: start && !isNaN(start.getTime()) ? start : undefined,
      end: end && !isNaN(end.getTime()) ? end : undefined,
      weekdays: val.weekdays,
    };
  })();

  const handleToggleValue = (value: any) => {
    const old = (column.getFilterValue() ?? []) as any[];
    let newFilter: any[];

    if (value === NO_VALUE_MARKER) {
      const includesNoValueMarker = old.includes(NO_VALUE_MARKER);
      if (includesNoValueMarker) {
        newFilter = old.filter((v: any) => v !== NO_VALUE_MARKER);
      } else {
        newFilter = [...old, NO_VALUE_MARKER];
      }
    } else {
      if (old.includes(value)) {
        newFilter = old.filter((v: any) => v !== value);
      } else {
        newFilter = [...old, value];
      }
    }
    column.setFilterValue(newFilter.length ? newFilter : undefined);
  };

  const handleDateChange = (field: "start" | "end", value: string) => {
    // Get fresh value directly from column to avoid closure staleness
    const raw = column.getFilterValue() as any;
    const currentFilter = {
      start: raw?.start ? new Date(raw.start) : undefined,
      end: raw?.end ? new Date(raw.end) : undefined,
      weekdays: raw?.weekdays,
    };

    const newFilter = { ...currentFilter };
    if (!value) {
      delete newFilter[field];
    } else if (field === "start") {
      newFilter.start = startOfDay(value);
    } else if (field === "end") {
      newFilter.end = endOfDay(value);
    }

    if (
      !newFilter.start &&
      !newFilter.end &&
      (!newFilter.weekdays || newFilter.weekdays.length === 0)
    ) {
      column.setFilterValue(undefined);
    } else {
      column.setFilterValue(newFilter);
    }
  };

  const handleWeekdayToggle = (day: number) => {
    // Get fresh value directly from column to avoid closure staleness
    const raw = column.getFilterValue() as any;
    const currentWeekdays = raw?.weekdays ?? [];

    let newWeekdays: number[];
    if (currentWeekdays.includes(day)) {
      newWeekdays = currentWeekdays.filter((d: number) => d !== day);
    } else {
      newWeekdays = [...currentWeekdays, day];
    }

    const currentFilter = {
      start: raw?.start ? new Date(raw.start) : undefined,
      end: raw?.end ? new Date(raw.end) : undefined,
      weekdays: newWeekdays,
    };

    if (
      !currentFilter.start &&
      !currentFilter.end &&
      newWeekdays.length === 0
    ) {
      column.setFilterValue(undefined);
    } else {
      column.setFilterValue(currentFilter);
    }
  };

  const handleInteraction = (isInteracting: boolean) => {
    onInteractionChange?.(isInteracting);
  };

  // If isMutuallyExclusive, all other options are disabled once one filter value is selected
  const isMutuallyExclusive = isEmailColumn
    ? false // force not-mutually-exclusive for email column
    : (mutuallyExclusive ?? uniqueValues.length <= 2); // use prop, or default to true if there are only 1 or 2 options

  if (isDateColumn) {
    return (
      <div
        style={{
          padding: "var(--wlcm-spacing-sm)",
          display: "flex",
          flexDirection: "column",
          gap: "var(--wlcm-spacing-xs)",
          height: "100%", // Ensure it fills the container
          overflowY: "auto", // Allow scrolling within the date filter if needed
        }}
      >
        <DateInput
          key={`start-${dateFilterValue.start ? dateFilterValue.start.toISOString() : "empty"}`}
          label={t("BUILDING_SETTINGS.BOOKING.EDIT_ROOM.WEEKDAYS.FROM")}
          value={
            dateFilterValue.start
              ? (format(dateFilterValue.start, "yyyy-MM-dd") as DateString)
              : undefined
          }
          onChange={(e) => handleDateChange("start", e.target.value)}
          maxDate={
            dateFilterValue.end
              ? (format(dateFilterValue.end, "yyyy-MM-dd") as DateString)
              : undefined
          }
          compact
          onFocus={() => handleInteraction(true)}
          onBlur={() => handleInteraction(false)}
        />
        <DateInput
          key={`end-${dateFilterValue.end ? dateFilterValue.end.toISOString() : "empty"}`}
          label={t("TO")}
          value={
            dateFilterValue.end
              ? (format(dateFilterValue.end, "yyyy-MM-dd") as DateString)
              : undefined
          }
          onChange={(e) => handleDateChange("end", e.target.value)}
          minDate={
            dateFilterValue.start
              ? (format(dateFilterValue.start, "yyyy-MM-dd") as DateString)
              : undefined
          }
          compact
          onFocus={() => handleInteraction(true)}
          onBlur={() => handleInteraction(false)}
        />
        <div className={styles.divider} />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--wlcm-spacing-xs)",
          }}
        >
          {ALL_WEEKDAYS.map((day) => (
            <div key={day} className={styles.checkboxItem}>
              <Checkbox
                label={getWeekdayFromNumber(day, i18n.language)}
                checked={dateFilterValue.weekdays?.includes(day) || false}
                onChange={() => handleWeekdayToggle(day)}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {uniqueValues.length >= 10 && (
        <div className={`${styles.checkboxItem} ${styles.searchContainer}`}>
          <InputField
            compact
            icon="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t("SEARCH")}
            autoFocus
          />
        </div>
      )}
      {filteredValues.map((value: any, idx) => {
        let label = String(value);
        if (getLabel) {
          label = String(getLabel(value, column));
        } else if (value === NO_VALUE_MARKER) {
          label = t("NO_VALUE");
        } else if (value === HAS_VALUE_MARKER) {
          label = t("HAS_VALUE");
        } else if (typeof value === "boolean") {
          label = value ? t("YES") : t("NO");
        } else if (!value) {
          label = t("NO_VALUE");
        }
        const isChecked =
          Array.isArray(filterValue) && (filterValue as any[]).includes(value);
        const disabled = isMutuallyExclusive && !!filterValue && !isChecked;

        return (
          <div key={idx} className={styles.checkboxItem}>
            <Checkbox
              label={label}
              checked={isChecked}
              disabled={disabled}
              onChange={() => handleToggleValue(value)}
            />
          </div>
        );
      })}
      {filteredValues.length === 0 && (
        <div
          className={styles.checkboxItem}
          style={{
            opacity: 0.5,
            textAlign: "center",
          }}
        >
          {t("NO_FILTERS_AVAILABLE")}
        </div>
      )}
    </div>
  );
}
